import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { MdSend, MdClose } from 'react-icons/md';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { campaignPostRequest, campaignPostReset } from '../../redux/slices/postNowSlice';
import { fetchIntegrationsRequest, updateTikTokSettingsRequest } from '../../redux/slices/integrationsSlice';
import PlatformSelector from './PlatformSelector';
import CampaignPostingProgress from './CampaignPostingProgress';
import TikTokPostSettingsCard from './TikTokPostSettingsCard';
import ImageProperties from '../playGround/ImageProperties';
import type { TikTokSettingsValues } from '../integrations/detail/TikTokSettingsPanel';
import type { PostNowFormProps, SocialMedium } from './types';

const PostNowForm: React.FC<PostNowFormProps> = ({
  flyerImageUrl,
  restaurantId,
  campaignId,
  onSubmit,
  mediaProperties,
  mediaType
}) => {
  const dispatch = useAppDispatch();
  const postNowState = useAppSelector((state) => state.postNow);
  const { data: apiIntegrations, updatingSettings, updateSettingsError } = useAppSelector((state) => state.integrations);
  const [selectedPlatforms, setSelectedPlatforms] = useState<SocialMedium[]>([]);
  const [postingRequestId, setPostingRequestId] = useState<string | null>(null);
  const wasSavingTikTokSettings = useRef(false);
  // Whether the TikTok default post settings panel is currently open
  const [showTikTokSettingsPanel, setShowTikTokSettingsPanel] = useState(false);
  // Whether the user has already viewed/saved TikTok settings once in this session
  const [hasSeenTikTokSettings, setHasSeenTikTokSettings] = useState(false);

  // Fetch integrations on mount so platform connection status is known
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchIntegrationsRequest({ restaurantId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  const tiktokIntegration = useMemo(() => {
    return apiIntegrations.find((int) => int.provider?.toLowerCase() === 'tiktok');
  }, [apiIntegrations]);

  const tiktokUserInfo = tiktokIntegration?.configuration?.userInfo || {};
  const tkDisplayName = tiktokUserInfo.display_name || tiktokIntegration?.configuration?.displayName;
  const tkUsername = tiktokUserInfo.username || tiktokIntegration?.configuration?.username;
  const tkAvatarUrl = tiktokUserInfo.avatar_large_url || tiktokUserInfo.avatar_url || tiktokIntegration?.configuration?.avatarUrl;
  const tkIsVerified = tiktokUserInfo.is_verified ?? tiktokIntegration?.configuration?.isVerified;

  const tkSettings = tiktokIntegration?.configuration?.settings;
  const privacyLevelMap: Record<string, string> = {
    PUBLIC_TO_EVERYONE: 'Public',
    MUTUAL_FOLLOW_FRIENDS: 'Friends',
    SELF_ONLY: 'Private',
  };

  const [tikTokSettings, setTikTokSettings] = useState<TikTokSettingsValues>({
    visibility: 'Public',
    allowComment: true,
    allowDuet: true,
    allowStitch: true,
    discloseContent: true,
    brandType: 'your-brand',
  });

  // Sync TikTok settings once integration data is available
  useEffect(() => {
    if (tkSettings) {
      setTikTokSettings({
        visibility: privacyLevelMap[tkSettings.privacyLevel] || 'Public',
        allowComment: !!tkSettings.disable_comment,
        allowDuet: !!tkSettings.disable_duet,
        allowStitch: !!tkSettings.disable_stitch,
        discloseContent: !!tkSettings.brand_content_toggle || !!tkSettings.brand_organic_toggle,
        brandType: tkSettings.brand_content_toggle ? 'branded-content' : 'your-brand',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiktokIntegration?.id]);

  const handlePlatformToggle = (platform: SocialMedium) => {
    if (platform === 'tiktok') {
      const isCurrentlySelected = selectedPlatforms.includes('tiktok');
      if (!isCurrentlySelected) {
        // Selecting TikTok
        setSelectedPlatforms(prev => [...prev, 'tiktok']);
        if (!hasSeenTikTokSettings) {
          // First time selecting TikTok - show the settings panel
          setShowTikTokSettingsPanel(true);
        }
      } else {
        // Deselecting TikTok
        setSelectedPlatforms(prev => prev.filter(p => p !== 'tiktok'));
        setShowTikTokSettingsPanel(false);
      }
      return;
    }

    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleTikTokInfoClick = () => {
    setShowTikTokSettingsPanel(true);
  };

  const handleSaveTikTokSettings = () => {
    if (!restaurantId || !tiktokIntegration?.id || !tikTokSettings.visibility) { return; }

    const privacyLevelReverseMap: Record<string, string> = {
      Public: 'PUBLIC_TO_EVERYONE',
      Friends: 'MUTUAL_FOLLOW_FRIENDS',
      Private: 'SELF_ONLY',
    };

    wasSavingTikTokSettings.current = true;
    dispatch(updateTikTokSettingsRequest({
      integrationId: tiktokIntegration.id,
      restaurantId,
      settings: {
        privacyLevel: privacyLevelReverseMap[tikTokSettings.visibility] || 'PUBLIC_TO_EVERYONE',
        disable_comment: tikTokSettings.allowComment,
        disable_duet: tikTokSettings.allowDuet,
        disable_stitch: tikTokSettings.allowStitch,
        brand_content_toggle: tikTokSettings.discloseContent && tikTokSettings.brandType === 'branded-content',
        brand_organic_toggle: tikTokSettings.discloseContent && tikTokSettings.brandType === 'your-brand',
      },
    }));
  };

  // Once the TikTok settings update completes successfully, close the settings panel
  // (keep TikTok selected) and remember that the user has already seen the settings
  useEffect(() => {
    if (wasSavingTikTokSettings.current && !updatingSettings) {
      wasSavingTikTokSettings.current = false;
      if (!updateSettingsError) {
        setHasSeenTikTokSettings(true);
        setShowTikTokSettingsPanel(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatingSettings]);

  // Handle Campaign Post API success
  useEffect(() => {
    if (postNowState.campaignPostSuccess && postNowState.campaignPostData) {
      const requestId = postNowState.campaignPostData?.requestId;
      if (requestId) {
        setPostingRequestId(requestId);
      } else {
      }
    }
  }, [postNowState.campaignPostSuccess, postNowState.campaignPostData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedPlatforms.length === 0) { return; }
    if (!campaignId) { return; }

    dispatch(campaignPostRequest({
      campaignId,
      postType: 'post_now',
      platforms: selectedPlatforms,
      restaurantId,
      mediaType,
    }));
  };

  const handlePostingComplete = () => {
    // Don't call onSubmit immediately - let the progress component handle redirect
    // The component will navigate to calendar after showing success message
    dispatch(campaignPostReset());
    // Don't reset requestId here - let it persist until redirect
  };

  const handlePostingError = () => {
    dispatch(campaignPostReset());
    setPostingRequestId(null);
  };

  const isFormValid = selectedPlatforms.length > 0;
  const isLoading = postNowState.campaignPostLoading;

  return (
    <>
      {/* Progress Monitor Overlay */}
      {postingRequestId && (
        <CampaignPostingProgress
          requestId={postingRequestId}
          restaurantId={restaurantId}
          onComplete={handlePostingComplete}
          onError={handlePostingError}
        />
      )}

      {/* Main Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Side: Flyer Preview */}
        <div className="lg:col-span-1">
          <h3 className="text-sm font-semibold text-gray-400 mb-3">Flyer Preview</h3>
          <div className="relative rounded-xl overflow-hidden shadow-lg border border-gray-700">
            <img
              src={flyerImageUrl}
              alt="Flyer Preview"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* Right Side: Platform Selection */}
        <div className="lg:col-span-2 space-y-6 flex flex-col ">
          <div className="flex items-center justify-between gap-1">
            <PlatformSelector
              selectedPlatforms={selectedPlatforms}
              onToggle={handlePlatformToggle}
              isTikTokActive={showTikTokSettingsPanel}
              showTikTokInfo={hasSeenTikTokSettings && !showTikTokSettingsPanel}
              onTikTokInfoClick={handleTikTokInfoClick}
            />

            {showTikTokSettingsPanel && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button type="button" onClick={handleSaveTikTokSettings}
                  disabled={!tikTokSettings.visibility || updatingSettings}
                  className="px-4 py-2 text-sm font-semibold text-brand-primary hover:text-[#d63d20] disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer">
                  {updatingSettings ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowTikTokSettingsPanel(false)}
                  disabled={updatingSettings}
                  title="Close TikTok post settings"
                  className="text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  <MdClose size={20} />
                </button>
              </div>
            )}
          </div>

          {/* Image/Video Meta Details - same as shown on Playground page (hidden while TikTok default post settings are open) */}
          {mediaProperties && !showTikTokSettingsPanel && (
            <ImageProperties properties={mediaProperties} compact />
          )}

          {/* TikTok Default Post Settings - shown only while the settings panel is open */}
          {showTikTokSettingsPanel && (
            <TikTokPostSettingsCard
              displayName={tkDisplayName}
              username={tkUsername}
              avatarUrl={tkAvatarUrl}
              isVerified={tkIsVerified}
              values={tikTokSettings}
              onChange={setTikTokSettings}
            />
          )}

        </div>


         
      </div>
       {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t-1 border-gray-700">
            <motion.button
              type="submit"
              whileHover={{ scale: (isFormValid && !isLoading) ? 1.02 : 1 }}
              whileTap={{ scale: (isFormValid && !isLoading) ? 0.98 : 1 }}
              disabled={!isFormValid || isLoading}
              className={`px-6 py-2.5 text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-colors ${
                (isFormValid && !isLoading)
                  ? 'bg-brand-primary hover:bg-[#d63d20] text-white cursor-pointer'
                  : 'bg-gray-700 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Posting...</span>
                </>
              ) : (
                <>
                  <MdSend size={18} />
                  Post Now
                </>
              )}
            </motion.button>
          </div>
      </form>
    </>
  );
};

export default PostNowForm;
